/**
 * INTERCEPTOR — top-down dodge-and-shoot pursuit racer (MVP).
 *
 * Heritage: Spy Hunter (top-down view, threat assessment, civilian vs
 * enemy traffic). Original mechanics: continuous lateral steering (NOT
 * lane-snap — distinct from NEON OVERPASS's continuous-but-arcadey
 * pseudo-3D steering); Switchblade enemy with telegraphed lunges.
 *
 * Top-down architecture (no perspective projection):
 *   - Road is a vertical scrolling strip; lane marks animate down to
 *     create the "moving forward" illusion
 *   - Player sits at fixed screen-Y near the bottom; world (traffic +
 *     enemies) flows down past them
 *   - Traffic relative-Y velocity = (player speed) - (traffic speed)
 *   - Collision: AABB between player rect and each traffic rect
 *
 * Determinism: all randomness routes through `ctx.random` (PRNG); no
 * `Math.random`, no clock reads. The same `(seed, inputFrames)` always
 * produces the same final score — verified by the determinism test.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  CIVILIAN_BLUE,
  CIVILIAN_GRAY,
  CIVILIAN_GREEN,
  CIVILIAN_WHITE,
  CRASH_FLASH,
  HUD_DISTANCE,
  HUD_ENEMY,
  HUD_SCORE,
  HUD_TEXT,
  LANE_MARK,
  PLAYER_BODY,
  PLAYER_BODY_DARK,
  PLAYER_TAIL,
  PLAYER_WINDOW,
  PLAYER_WINDOW_HI,
  ROAD_DARK,
  ROAD_EDGE,
  ROAD_LIGHT,
  SHOULDER_GRASS,
  SWITCHBLADE_BODY,
  SWITCHBLADE_DARK,
  SWITCHBLADE_HEADLIGHT,
  SWITCHBLADE_WARNING,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;

// Road geometry.
const SHOULDER_W = 56; // grass on each side
const ROAD_X = SHOULDER_W;
const ROAD_W = WIDTH - SHOULDER_W * 2; // 368
const ROAD_EDGE_W = 4; // colored road edge line
const LANES = 3;
const LANE_W = ROAD_W / LANES;
const LANE_MARK_LEN = 24;
const LANE_MARK_GAP = 24;
const LANE_MARK_PERIOD = LANE_MARK_LEN + LANE_MARK_GAP;

// Player.
const PLAYER_W = 22;
const PLAYER_H = 32;
const PLAYER_SCREEN_Y = HEIGHT - 60;
// Lateral physics (sc-2815 tuning pass): softer accel, longer glide on
// release, lower max lateral speed for finer positioning. Original
// values (0.7 / 0.86 / 5.0) felt staccato — like the car was snapping
// between positions instead of drifting.
const PLAYER_STEER_ACCEL = 0.4; // px/tick² added to lateral velocity per held tick
const PLAYER_STEER_DAMP = 0.92; // multiplied each tick when no input
const PLAYER_MAX_LAT_VEL = 3.8; // px/tick lateral cap

// World speed (vertical scroll). Player can accelerate / brake within
// bounds. World scrolls at `worldSpeed` px/tick; traffic ABSOLUTE speeds
// are slower (visible drifting down) or faster (overtaking the player).
const WORLD_BASE_SPEED = 3.0; // px/tick at no input
const WORLD_ACCEL = 0.06; // when up held
const WORLD_BRAKE = -0.12; // when down held
const WORLD_DECEL = -0.03; // natural drag toward base
const WORLD_MIN_SPEED = 1.5;
const WORLD_MAX_SPEED = 5.5;

// Traffic.
const TRAFFIC_W = 22;
const TRAFFIC_H = 32;
const TRAFFIC_SPAWN_INTERVAL_BASE = 70; // ticks
const TRAFFIC_SPAWN_INTERVAL_VAR = 30;
const TRAFFIC_BASE_SPEED = 1.2; // absolute downward px/tick (slow)
const TRAFFIC_SPEED_VAR = 1.4;
const TRAFFIC_FAST_CHANCE = 0.18; // some traffic OVERTAKES the player
const TRAFFIC_FAST_SPEED = 4.6; // faster than world; passes from behind
const TRAFFIC_OFFSCREEN_MARGIN = TRAFFIC_H * 1.5;

// Switchblade enemy.
const SWITCHBLADE_W = 22;
const SWITCHBLADE_H = 32;
const SWITCHBLADE_SPAWN_INTERVAL = 360; // ticks (~6s)
const SWITCHBLADE_LUNGE_COOLDOWN = 90; // ticks between lunges
const SWITCHBLADE_LUNGE_WARNING = 30; // ticks of warning blink before lunge
// Lunge velocity is capped (sc-2815 tuning): scaling velocity with
// difficulty made high-zone Switchblades overshoot the player entirely
// (12+ px/tick × 18 ticks = 220+ px lateral, wider than the road).
// Instead, difficulty extends the lunge DURATION so the enemy commits
// further but stays survivable per-tick.
const SWITCHBLADE_LUNGE_VEL = 3.6; // lateral velocity during lunge (constant)
const SWITCHBLADE_LUNGE_DURATION = 22; // ticks of active lunge at zone 1
// Switchblade matches player's world speed (NOT a low fixed value) so it
// PACES the player — visualVy ≈ 0, the enemy hangs next to the player and
// threatens lateral lunges. Originally 0.8 px/tick meant visualVy ≈ 4-5
// px/tick at full player speed → enemy raced past the player every time
// the lunge fired, even when correctly aimed.
const SWITCHBLADE_PACE_OFFSET = 0.05; // how much SLOWER than player (so it slowly approaches)

// Crash + score.
const CRASH_FLASH_TICKS = 18;
const SHAKE_TICKS = 14;
const SHAKE_MAGNITUDE = 3;
const SCORE_PER_ENEMY_PASSED = 100;

// Difficulty progression (sc-2815). Every ZONE_DISTANCE world units the
// "zone" advances by 1, increasing spawn rates and enemy aggression by
// ZONE_RAMP per zone. Hard cap at MAX_ZONES so things stay survivable
// enough to feel like a hard run, not impossible.
const ZONE_DISTANCE = 4000;
const ZONE_RAMP = 0.3; // +30% pressure per zone
const MAX_ZONES = 8;
const ZONE_BANNER_TICKS = 90; // ~1.5s banner showing "ZONE N"

type Phase = 'playing' | 'crashed' | 'game-over';

interface TrafficCar {
  x: number;
  y: number;
  vy: number; // absolute world-Y velocity (positive = down)
  color: string;
  width: number;
  height: number;
}

interface Switchblade extends TrafficCar {
  /** Ticks until next lunge attempt. */
  lungeCooldown: number;
  /** > 0 while warning blink is showing. */
  warningTicks: number;
  /** Sign of next lunge: -1 = left, +1 = right. */
  lungeDirection: number;
  /** > 0 while actively lunging. */
  lungeTicks: number;
  /** Whether this enemy has been counted as "passed" (off-screen behind). */
  countedPassed: boolean;
  /** True once this Switchblade has fired its single lunge and is
   *  disengaging — falls back behind the player to be passed. */
  disengaged: boolean;
}

export class InterceptorInstance implements GameInstance {
  private readonly ctx: GameContext;

  // World state.
  private worldSpeed = WORLD_BASE_SPEED;
  private scrollY = 0;
  private distance = 0; // accumulated world units

  // Player.
  private playerX = WIDTH / 2 - PLAYER_W / 2;
  private playerVX = 0;
  private inLeft = false;
  private inRight = false;
  private inUp = false;
  private inDown = false;

  // Traffic + enemies.
  private traffic: TrafficCar[] = [];
  private switchblades: Switchblade[] = [];
  // Grace period: 2s of empty road at round start so the player can find
  // their lane before threats start arriving. Without this, zone-1 deaths
  // happen in the first second from a civilian spawning directly into the
  // player's lane.
  private trafficSpawnTimer = 120;
  private switchbladeSpawnTimer = 300; // first enemy ~5s in

  // Score.
  private score = 0;
  private enemiesPassed = 0;

  // Difficulty progression.
  private currentZone = 1;
  private zoneBannerTicks = ZONE_BANNER_TICKS; // banner for "ZONE 1" on start

  // Engine drone (sc-2868 polish). Continuous detuned voices modulated
  // by worldSpeed. Lazy-started on first update tick; stopped on teardown.
  private engineCarrier: LoopHandle | null = null;
  private engineDetune: LoopHandle | null = null;

  // Lifecycle.
  private phase: Phase = 'playing';
  private crashFlashLeft = 0;
  private shakeTicksLeft = 0;
  private finalScore = 0;
  private finalDistance = 0;
  private finalEnemiesPassed = 0;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inUp = state.up || state.fireA;
    this.inDown = state.down;
  }

  update(_dt: number): void {
    if (this.phase === 'game-over') {
      // Engine should be silent on game-over (player is dead).
      this.engineCarrier?.setGain(0);
      this.engineDetune?.setGain(0);
      return;
    }

    if (this.phase === 'crashed') {
      this.crashFlashLeft -= 1;
      if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;
      // Cut engine during crash flash so the high-RPM pitch doesn't ring.
      this.engineCarrier?.setGain(0);
      this.engineDetune?.setGain(0);
      if (this.crashFlashLeft <= 0) {
        this.phase = 'game-over';
        this.finalScore = Math.floor(this.score);
        this.finalDistance = Math.floor(this.distance);
        this.finalEnemiesPassed = this.enemiesPassed;
        this.ctx.events.emit('death', { remainingLives: 0 });
      }
      return;
    }

    if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;

    // Update world speed from input. Capped at min/max.
    if (this.inUp) this.worldSpeed += WORLD_ACCEL;
    else if (this.inDown) this.worldSpeed += WORLD_BRAKE;
    else this.worldSpeed += WORLD_DECEL;
    if (this.worldSpeed < WORLD_MIN_SPEED) this.worldSpeed = WORLD_MIN_SPEED;
    if (this.worldSpeed > WORLD_MAX_SPEED) this.worldSpeed = WORLD_MAX_SPEED;

    // Engine drone: continuous detuned voices that follow worldSpeed.
    // INTERCEPTOR has a higher pitch range than NEON OVERPASS to read as
    // a different car/cartridge (90-260Hz vs 70-280Hz).
    const speedFrac =
      (this.worldSpeed - WORLD_MIN_SPEED) / (WORLD_MAX_SPEED - WORLD_MIN_SPEED);
    const engineHz = 90 + 170 * speedFrac;
    if (!this.engineCarrier) {
      this.engineCarrier = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: engineHz,
        gain: 0.16,
        fm: { ratio: 1.5, index: 10 },
      });
      this.engineDetune = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: engineHz,
        gain: 0.08,
        detune: 9,
      });
    }
    this.engineCarrier.setFrequency(engineHz);
    this.engineDetune?.setFrequency(engineHz);
    const engineGain = 0.1 + 0.12 * speedFrac;
    this.engineCarrier.setGain(engineGain);
    this.engineDetune?.setGain(engineGain * 0.5);

    // Scroll lane marks downward (cosmetic).
    this.scrollY = (this.scrollY + this.worldSpeed) % LANE_MARK_PERIOD;

    // Accumulate distance + base score (distance score).
    this.distance += this.worldSpeed;
    this.score += this.worldSpeed * 0.2;

    // Zone progression. Every ZONE_DISTANCE units the zone advances and a
    // banner pops. Difficulty factor below derives from currentZone.
    const targetZone = Math.min(
      MAX_ZONES,
      1 + Math.floor(this.distance / ZONE_DISTANCE)
    );
    if (targetZone > this.currentZone) {
      this.currentZone = targetZone;
      this.zoneBannerTicks = ZONE_BANNER_TICKS;
      this.ctx.audio.sfx('zone-up');
    }
    if (this.zoneBannerTicks > 0) this.zoneBannerTicks -= 1;

    // Steering.
    if (this.inLeft && !this.inRight) {
      this.playerVX -= PLAYER_STEER_ACCEL;
    } else if (this.inRight && !this.inLeft) {
      this.playerVX += PLAYER_STEER_ACCEL;
    } else {
      this.playerVX *= PLAYER_STEER_DAMP;
      if (Math.abs(this.playerVX) < 0.05) this.playerVX = 0;
    }
    if (this.playerVX > PLAYER_MAX_LAT_VEL) this.playerVX = PLAYER_MAX_LAT_VEL;
    if (this.playerVX < -PLAYER_MAX_LAT_VEL) this.playerVX = -PLAYER_MAX_LAT_VEL;
    this.playerX += this.playerVX;

    // Clamp player to road (with a small overflow into shoulder so the
    // grass edge feels like a soft penalty instead of a hard wall).
    const minX = ROAD_X - 6;
    const maxX = ROAD_X + ROAD_W - PLAYER_W + 6;
    if (this.playerX < minX) {
      this.playerX = minX;
      this.playerVX = 0;
    }
    if (this.playerX > maxX) {
      this.playerX = maxX;
      this.playerVX = 0;
    }

    // Advance traffic. Each car's effective Y velocity vs the player:
    //   visualVy = car.vy - worldSpeed
    // Most traffic has vy < worldSpeed → visualVy < 0 → drifts toward
    // player (down the screen relative to camera). "Fast" traffic has
    // vy > worldSpeed → overtakes player from behind.
    for (const c of this.traffic) {
      const visualVy = this.worldSpeed - c.vy;
      c.y += visualVy;
    }
    // Drop off-screen.
    this.traffic = this.traffic.filter(
      (c) => c.y > -TRAFFIC_OFFSCREEN_MARGIN && c.y < HEIGHT + TRAFFIC_OFFSCREEN_MARGIN
    );

    // Spawn civilian traffic. Spawn interval shrinks with zone.
    const difficulty = 1 + (this.currentZone - 1) * ZONE_RAMP;
    this.trafficSpawnTimer -= 1;
    if (this.trafficSpawnTimer <= 0) {
      this.spawnTraffic();
      this.trafficSpawnTimer = Math.floor(
        (TRAFFIC_SPAWN_INTERVAL_BASE + this.ctx.random.int(0, TRAFFIC_SPAWN_INTERVAL_VAR)) /
          difficulty
      );
    }

    // Advance switchblades — Switchblades PACE the player until they fire
    // their one lunge, then DISENGAGE (drift behind the player to be
    // passed). Pacing: vy = worldSpeed - small offset → visualVy small
    // positive → enemy creeps down toward player. Disengaging: vy far
    // less than worldSpeed → visualVy large positive → enemy slides off
    // the bottom and gets counted as passed.
    for (const s of this.switchblades) {
      if (s.disengaged) {
        // Fall behind the player.
        s.vy = Math.max(0.5, this.worldSpeed * 0.3);
      } else if (s.y < PLAYER_SCREEN_Y - 80) {
        // Approaching from above — significantly slower than world so it
        // slides DOWN the screen into the player's threat zone within a
        // couple seconds. Once it reaches threat range, pacing kicks in
        // below.
        s.vy = Math.max(0.5, this.worldSpeed - 2.4);
      } else {
        // In threat zone — sit next to the player.
        s.vy = this.worldSpeed - SWITCHBLADE_PACE_OFFSET;
      }
      const visualVy = this.worldSpeed - s.vy;
      s.y += visualVy;

      // Lunge state machine.
      if (s.lungeTicks > 0) {
        // Actively lunging — apply lateral velocity toward target dir.
        s.x += s.lungeDirection * SWITCHBLADE_LUNGE_VEL;
        s.lungeTicks -= 1;
        // Stop the lunge early if the Switchblade reaches the player's
        // X position — prevents overshooting past the player when
        // duration is long at high zones.
        const playerCenterX = this.playerX + PLAYER_W / 2;
        const enemyCenterX = s.x + SWITCHBLADE_W / 2;
        const reachedPlayer =
          (s.lungeDirection === 1 && enemyCenterX >= playerCenterX) ||
          (s.lungeDirection === -1 && enemyCenterX <= playerCenterX);
        if (s.lungeTicks <= 0 || reachedPlayer) {
          s.lungeTicks = 0;
          s.lungeCooldown = SWITCHBLADE_LUNGE_COOLDOWN;
          // One-shot enemy: after the lunge it disengages and falls behind
          // the player. Becomes a "passed" tally + a possible collider
          // for any car catching up to the player.
          s.disengaged = true;
        }
      } else if (s.warningTicks > 0) {
        // Warning phase — blinking, holding position.
        s.warningTicks -= 1;
        if (s.warningTicks <= 0) {
          // Lunge duration scales with difficulty so high-zone enemies
          // commit further before giving up. Velocity stays constant.
          s.lungeTicks = Math.floor(SWITCHBLADE_LUNGE_DURATION * difficulty);
        }
      } else if (s.lungeCooldown > 0) {
        s.lungeCooldown -= 1;
      } else {
        // Ready to lunge — only if on screen.
        if (s.y > 0 && s.y < HEIGHT - SWITCHBLADE_H) {
          s.warningTicks = SWITCHBLADE_LUNGE_WARNING;
          s.lungeDirection = this.playerX + PLAYER_W / 2 < s.x + SWITCHBLADE_W / 2 ? -1 : 1;
          this.ctx.audio.sfx('warn');
        }
      }

      // Clamp to road bounds.
      if (s.x < ROAD_X) s.x = ROAD_X;
      if (s.x > ROAD_X + ROAD_W - SWITCHBLADE_W) s.x = ROAD_X + ROAD_W - SWITCHBLADE_W;

      // Count as passed when fully off-screen below.
      if (!s.countedPassed && s.y > HEIGHT + SWITCHBLADE_H) {
        s.countedPassed = true;
        this.enemiesPassed += 1;
        this.score += SCORE_PER_ENEMY_PASSED;
        this.ctx.events.emit('score', { score: Math.floor(this.score), delta: SCORE_PER_ENEMY_PASSED });
      }
    }
    this.switchblades = this.switchblades.filter(
      (s) => s.y < HEIGHT + TRAFFIC_OFFSCREEN_MARGIN * 3
    );

    // Spawn switchblades.
    this.switchbladeSpawnTimer -= 1;
    if (this.switchbladeSpawnTimer <= 0) {
      this.spawnSwitchblade();
      this.switchbladeSpawnTimer = Math.floor(SWITCHBLADE_SPAWN_INTERVAL / difficulty);
    }

    // Collision check — only meaningful during 'playing'.
    if (this.collides()) {
      this.phase = 'crashed';
      this.crashFlashLeft = CRASH_FLASH_TICKS;
      this.shakeTicksLeft = SHAKE_TICKS;
      this.ctx.audio.sfx('crash');
    }
  }

  render(g: CanvasRenderingContext2D): void {
    // Screen-shake on collision. PRNG-free derivation from shake counter.
    if (this.shakeTicksLeft > 0) {
      const t = this.shakeTicksLeft;
      const intensity = (t / SHAKE_TICKS) * SHAKE_MAGNITUDE;
      const sx = Math.sin(t * 12.91) * intensity;
      const sy = Math.cos(t * 9.37) * intensity;
      g.save();
      g.translate(Math.round(sx), Math.round(sy));
    }

    // Shoulders (grass).
    g.fillStyle = SHOULDER_GRASS;
    g.fillRect(0, 0, SHOULDER_W, HEIGHT);
    g.fillRect(WIDTH - SHOULDER_W, 0, SHOULDER_W, HEIGHT);

    // Road surface — two-tone vertical stripe to give a sense of depth.
    g.fillStyle = ROAD_DARK;
    g.fillRect(ROAD_X, 0, ROAD_W, HEIGHT);
    g.fillStyle = ROAD_LIGHT;
    // Subtle lighter strip down the middle of the road for vertical depth.
    g.fillRect(ROAD_X + ROAD_W / 3, 0, ROAD_W / 3, HEIGHT);

    // Road edge lines.
    g.fillStyle = ROAD_EDGE;
    g.fillRect(ROAD_X, 0, ROAD_EDGE_W, HEIGHT);
    g.fillRect(ROAD_X + ROAD_W - ROAD_EDGE_W, 0, ROAD_EDGE_W, HEIGHT);

    // Lane marks (animated).
    g.fillStyle = LANE_MARK;
    const markX1 = ROAD_X + LANE_W;
    const markX2 = ROAD_X + LANE_W * 2;
    for (let y = -LANE_MARK_PERIOD + this.scrollY; y < HEIGHT; y += LANE_MARK_PERIOD) {
      g.fillRect(markX1 - 1, y, 2, LANE_MARK_LEN);
      g.fillRect(markX2 - 1, y, 2, LANE_MARK_LEN);
    }

    // Traffic.
    for (const c of this.traffic) {
      this.drawCar(g, c.x, c.y, c.width, c.height, c.color);
    }

    // Switchblades.
    for (const s of this.switchblades) {
      this.drawSwitchblade(g, s);
    }

    // Player.
    this.drawPlayer(g);

    // Crash flash overlay.
    if (this.phase === 'crashed' && this.crashFlashLeft > 0) {
      const a = this.crashFlashLeft / CRASH_FLASH_TICKS;
      g.globalAlpha = a * 0.7;
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    // Pop the shake before HUD.
    if (this.shakeTicksLeft > 0) {
      g.restore();
    }

    this.renderHUD(g);

    if (this.phase === 'game-over') {
      this.renderGameOver(g);
    }
  }

  resize(_w: number, _h: number): void {
    /* 480x270 internal — cabinet scales */
  }

  pause(): void {
    /* no audio yet */
  }

  resume(): void {
    /* no audio yet */
  }

  teardown(): void {
    this.traffic = [];
    this.switchblades = [];
    this.engineCarrier?.stop();
    this.engineDetune?.stop();
    this.engineCarrier = null;
    this.engineDetune = null;
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  /** Cabinet polls this to know when to start a new round. */
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // --------------------------------------------------------------------------
  // Internal helpers
  // --------------------------------------------------------------------------

  private spawnTraffic(): void {
    // Player's current lane — civilians never spawn into it from the top
    // (slow traffic, drifts down INTO player). Fast traffic overtakes from
    // below and is naturally evadable. This "personal bubble" rule is the
    // single biggest factor in fairness: a slow civilian appearing 270px
    // above the player in their lane is unavoidable at high world speed.
    const playerCenterX = this.playerX + PLAYER_W / 2;
    const playerLane = Math.max(
      0,
      Math.min(LANES - 1, Math.floor((playerCenterX - ROAD_X) / LANE_W))
    );

    // Fast traffic comes from behind — chance scales with zone.
    const fastChance = TRAFFIC_FAST_CHANCE + (this.currentZone - 1) * 0.04;
    const isFast = this.ctx.random.next() < fastChance;

    // Choose lane. Slow traffic excludes the player's lane so it never
    // materializes directly above them. Fast traffic can use any lane —
    // the player has plenty of warning time as it climbs from below.
    let lane = this.ctx.random.int(0, LANES - 1);
    if (!isFast && lane === playerLane) {
      // Bump to an adjacent lane.
      lane = playerLane === 0 ? 1 : playerLane === LANES - 1 ? LANES - 2 : (this.ctx.random.next() < 0.5 ? playerLane - 1 : playerLane + 1);
    }
    const laneCenterX = ROAD_X + LANE_W * (lane + 0.5);
    const x = laneCenterX - TRAFFIC_W / 2 + (this.ctx.random.next() - 0.5) * 8;

    const colors = [CIVILIAN_BLUE, CIVILIAN_GREEN, CIVILIAN_WHITE, CIVILIAN_GRAY];
    const color = colors[this.ctx.random.int(0, colors.length - 1)] as string;

    const vy = isFast
      ? TRAFFIC_FAST_SPEED + this.ctx.random.next() * 0.8
      : TRAFFIC_BASE_SPEED + this.ctx.random.next() * TRAFFIC_SPEED_VAR;

    // Slow cars enter from the top of screen (in front of player).
    // Fast cars enter from below (behind player, overtaking).
    const y = isFast ? HEIGHT + TRAFFIC_H : -TRAFFIC_H;

    this.traffic.push({
      x,
      y,
      vy,
      color,
      width: TRAFFIC_W,
      height: TRAFFIC_H,
    });
  }

  private spawnSwitchblade(): void {
    // 35% chance to spawn in an adjacent lane to the player (the
    // Switchblade pursues laterally via the lunge anyway, so it doesn't
    // need to spawn directly above the player — that just means an
    // unavoidable hit if the player is mid-correction). 65% chance
    // random non-player lane. Player-lane spawns are explicitly excluded.
    const playerCenterX = this.playerX + PLAYER_W / 2;
    const playerLane = Math.max(
      0,
      Math.min(LANES - 1, Math.floor((playerCenterX - ROAD_X) / LANE_W))
    );
    let lane = this.ctx.random.int(0, LANES - 1);
    if (lane === playerLane) {
      // Bump out of player's lane.
      lane = playerLane === 0 ? 1 : playerLane === LANES - 1 ? LANES - 2 : (this.ctx.random.next() < 0.5 ? playerLane - 1 : playerLane + 1);
    }
    const laneCenterX = ROAD_X + LANE_W * (lane + 0.5);
    const x = laneCenterX - SWITCHBLADE_W / 2;
    // Spawn ABOVE visible area (y = -SWITCHBLADE_H * 2) but with vy set so
    // the enemy catches up to the player's Y over a few seconds. Need
    // visualVy > 0 during the approach so the Switchblade slides DOWN
    // the screen into the player's view, then pacing kicks in. We set
    // an "approach" velocity below worldSpeed so visualVy is small but
    // positive — once on-screen, the per-tick repacing in update() will
    // settle it next to the player.
    this.switchblades.push({
      x,
      y: -SWITCHBLADE_H * 2,
      vy: Math.max(0.5, this.worldSpeed - 1.2),
      color: SWITCHBLADE_BODY,
      width: SWITCHBLADE_W,
      height: SWITCHBLADE_H,
      lungeCooldown: 60,
      warningTicks: 0,
      lungeDirection: 0,
      lungeTicks: 0,
      countedPassed: false,
      disengaged: false,
    });
  }

  private collides(): boolean {
    const px0 = this.playerX;
    const px1 = this.playerX + PLAYER_W;
    const py0 = PLAYER_SCREEN_Y;
    const py1 = PLAYER_SCREEN_Y + PLAYER_H;

    const hit = (other: { x: number; y: number; width: number; height: number }): boolean => {
      const ox0 = other.x;
      const ox1 = other.x + other.width;
      const oy0 = other.y;
      const oy1 = other.y + other.height;
      return px0 < ox1 && px1 > ox0 && py0 < oy1 && py1 > oy0;
    };

    for (const c of this.traffic) {
      if (hit(c)) return true;
    }
    for (const s of this.switchblades) {
      if (hit(s)) return true;
    }
    return false;
  }

  private drawCar(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    color: string
  ): void {
    // Top-down car: body rect, lighter roof, dark windshield strip.
    g.fillStyle = '#000';
    g.fillRect(x + 1, y + 1, w, h); // shadow
    g.fillStyle = color;
    g.fillRect(x, y, w, h);
    g.fillStyle = '#1a3a55';
    g.fillRect(x + 3, y + 4, w - 6, 6); // front window
    g.fillRect(x + 3, y + h - 10, w - 6, 6); // rear window
    g.fillStyle = '#222';
    g.fillRect(x - 1, y + 4, 2, 6); // left wheel front
    g.fillRect(x + w - 1, y + 4, 2, 6);
    g.fillRect(x - 1, y + h - 10, 2, 6); // left wheel rear
    g.fillRect(x + w - 1, y + h - 10, 2, 6);
  }

  private drawSwitchblade(g: CanvasRenderingContext2D, s: Switchblade): void {
    // Same silhouette as civilian, but with the warning blink + headlights
    // that read as "threat" on the road.
    const blinking = s.warningTicks > 0 && s.warningTicks % 6 < 3;
    g.fillStyle = '#000';
    g.fillRect(s.x + 1, s.y + 1, s.width, s.height);
    g.fillStyle = blinking ? SWITCHBLADE_WARNING : SWITCHBLADE_BODY;
    g.fillRect(s.x, s.y, s.width, s.height);
    g.fillStyle = SWITCHBLADE_DARK;
    g.fillRect(s.x + 3, s.y + s.height - 10, s.width - 6, 6); // back hood
    g.fillStyle = '#1a3a55';
    g.fillRect(s.x + 3, s.y + 4, s.width - 6, 6); // front window
    g.fillStyle = SWITCHBLADE_HEADLIGHT;
    g.fillRect(s.x + 2, s.y, 4, 3); // headlights
    g.fillRect(s.x + s.width - 6, s.y, 4, 3);
    g.fillStyle = '#222';
    g.fillRect(s.x - 1, s.y + 4, 2, 6);
    g.fillRect(s.x + s.width - 1, s.y + 4, 2, 6);
    g.fillRect(s.x - 1, s.y + s.height - 10, 2, 6);
    g.fillRect(s.x + s.width - 1, s.y + s.height - 10, 2, 6);
  }

  private drawPlayer(g: CanvasRenderingContext2D): void {
    const x = this.playerX;
    const y = PLAYER_SCREEN_Y;
    // Body shadow.
    g.fillStyle = '#000';
    g.fillRect(x + 1, y + 1, PLAYER_W, PLAYER_H);
    // Body.
    g.fillStyle = PLAYER_BODY;
    g.fillRect(x, y, PLAYER_W, PLAYER_H);
    // Darker rear.
    g.fillStyle = PLAYER_BODY_DARK;
    g.fillRect(x + 3, y + PLAYER_H - 10, PLAYER_W - 6, 6);
    // Front windshield.
    g.fillStyle = PLAYER_WINDOW;
    g.fillRect(x + 3, y + 4, PLAYER_W - 6, 6);
    g.fillStyle = PLAYER_WINDOW_HI;
    g.fillRect(x + 4, y + 4, PLAYER_W - 8, 2);
    // Taillights — top of car (we look from above; rear is at bottom).
    g.fillStyle = PLAYER_TAIL;
    g.fillRect(x + 2, y + PLAYER_H - 4, 4, 3);
    g.fillRect(x + PLAYER_W - 6, y + PLAYER_H - 4, 4, 3);
    // Wheels.
    g.fillStyle = '#0a0a0a';
    g.fillRect(x - 1, y + 4, 2, 6);
    g.fillRect(x + PLAYER_W - 1, y + 4, 2, 6);
    g.fillRect(x - 1, y + PLAYER_H - 10, 2, 6);
    g.fillRect(x + PLAYER_W - 1, y + PLAYER_H - 10, 2, 6);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 18);

    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText(`ZONE ${this.currentZone}`, WIDTH / 2, 6);
    g.fillStyle = HUD_DISTANCE;
    g.fillText(String(Math.floor(this.distance / 100)).padStart(5, '0'), WIDTH / 2, 18);

    g.textAlign = 'right';
    g.fillStyle = HUD_TEXT;
    g.fillText('PASSED', WIDTH - 8, 6);
    g.fillStyle = HUD_ENEMY;
    g.fillText(String(this.enemiesPassed).padStart(3, '0'), WIDTH - 8, 18);

    g.textAlign = 'left';

    // Zone-advance banner — flashes when entering a new zone.
    if (this.zoneBannerTicks > 0) {
      const t = this.zoneBannerTicks / ZONE_BANNER_TICKS;
      // Fade in (last 1/3) then fade out (first 2/3) so the banner pops
      // briefly without lingering.
      const a = t > 0.66 ? (1 - t) * 3 : t / 0.66;
      g.globalAlpha = a;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = 'bold 28px ui-monospace, monospace';
      g.fillStyle = HUD_ENEMY;
      g.fillText(`ZONE ${this.currentZone}`, WIDTH / 2, HEIGHT / 2 - 10);
      g.font = '10px ui-monospace, monospace';
      g.fillStyle = HUD_TEXT;
      g.fillText('SPEED UP', WIDTH / 2, HEIGHT / 2 + 14);
      g.globalAlpha = 1;
      g.textAlign = 'left';
      g.textBaseline = 'top';
    }
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    g.fillStyle = CRASH_FLASH;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 30);

    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SCORE ${String(this.finalScore).padStart(6, '0')}`, WIDTH / 2, HEIGHT / 2);
    g.fillStyle = HUD_DISTANCE;
    g.fillText(
      `DISTANCE ${String(Math.floor(this.finalDistance / 100)).padStart(5, '0')}`,
      WIDTH / 2,
      HEIGHT / 2 + 16
    );
    g.fillStyle = HUD_ENEMY;
    g.fillText(`PASSED ${this.finalEnemiesPassed}`, WIDTH / 2, HEIGHT / 2 + 32);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 58);
  }
}
